import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Modal, Button, ModalBody, Row, Col } from 'reactstrap';
import { getPlural } from 'client/utils/plural';
import { isNil } from 'lodash-es';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { modalMousedownTrackingFix } from 'client/site-modules/shared/utils/modal-tracking-fix';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { getNoVehicleImageToDisplay } from 'site-modules/shared/utils/get-no-vehicle-image-to-display';
import { ModalHeader } from 'site-modules/lease-deals/components/lease-deals-details-modal/modal-header/modal-header';
import { DealInsights } from 'site-modules/lease-deals/components/deal-insights/deal-insights';
import { AboutLease } from 'site-modules/lease-deals/components/lease-deals-details-modal/about-lease/about-lease';
import { AboutVehicle } from 'site-modules/lease-deals/components/lease-deals-details-modal/about-vehicle/about-vehicle';
import { StickyLeaseDealsCta } from 'site-modules/lease-deals/components/lease-deals-details-modal/sticky-lease-deals-cta/sticky-lease-deals-cta';

import './lease-deals-details-modal.scss';

const DISCLAIMER_COLLAPSE_THRESHOLD = 600;

function getOfferExpiryText(daysRemaining) {
  if (isNil(daysRemaining) || daysRemaining > 7 || daysRemaining < 0) return null;
  if (daysRemaining === 0) return 'Offer expires today';
  return `Offer expires in ${daysRemaining} ${getPlural('day', daysRemaining)}`;
}

export function LeaseDealsDetailsModal({
  id,
  make,
  year,
  term,
  trim = undefined,
  msrp,
  model,
  photo = undefined,
  isOpen,
  onClose,
  payment,
  webUrls = undefined,
  endDate,
  bodyType = undefined,
  isMobile,
  driveType = undefined,
  engineType = undefined,
  disclaimer = undefined,
  dueAtSigning,
  makeNiceName,
  lifetimeCost = undefined,
  modelNiceName,
  seatingCapacity = undefined,
  mileageYearlyExcess = undefined,
  styleId,
  effectivePaymentRatio,
  effectivePaymentMedian = undefined,
  effectivePayment,
  isShop4931ZeroDownRoute = false,
  electricityRange = undefined,
  combinedMpg = undefined,
  edmundsRating = undefined,
  daysRemaining = undefined,
  showCta = false,
}) {
  const errorImage = getNoVehicleImageToDisplay(bodyType);

  const ctaRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const offerExpiryText = getOfferExpiryText(daysRemaining);
  const displayedDueAtSigning = isShop4931ZeroDownRoute ? effectivePayment : dueAtSigning;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      onMouseDown={modalMousedownTrackingFix}
      contentClassName={classNames('mt-3_5', {
        'mx-0 rounded-bottom-0 border-0': isMobile,
        'mx-0_5': !isMobile,
      })}
      modalClassName="lease-deals-details-modal overflow-hidden"
      data-tracking-parent="edm-lease-deals-vehicle-detail"
      labelledBy="lease-deals-modal-title"
    >
      <h2 id="lease-deals-modal-title" className="visually-hidden">
        {year} {make} {model}
        {trim ? ` ${trim}` : ''} lease deal details
      </h2>

      <Button
        color="link"
        onClick={onClose}
        aria-label="Close modal"
        className="close-button pos-a right-0 p-0 text-white size-24"
      >
        <span className="icon-cross2" aria-hidden />
      </Button>

      <ModalBody className="pos-r d-flex flex-column p-1">
        <div
          ref={scrollContainerRef}
          className={classNames('modal-body-wrapper overflow-y-auto pos-r', { 'p-0_5': isMobile, 'p-1': !isMobile })}
        >
          <ModalHeader
            ctaRef={ctaRef}
            id={id}
            year={year}
            make={make}
            model={model}
            trim={trim}
            payment={payment}
            effectivePayment={effectivePayment}
            dueAtSigning={dueAtSigning}
            msrp={msrp}
            term={term}
            styleId={styleId}
            isShop4931ZeroDownRoute={isShop4931ZeroDownRoute}
            endDate={endDate}
            photo={photo}
            errorImage={errorImage}
            isMobile={isMobile}
            showCta={showCta}
            disclaimer={disclaimer}
            webUrls={webUrls}
          />
          <div className="mt-2 mb-1">
            <DealInsights
              effectivePaymentRatio={effectivePaymentRatio}
              effectivePayment={effectivePayment}
              effectivePaymentMedian={effectivePaymentMedian}
              dueAtSigning={dueAtSigning}
              bodyType={bodyType}
              term={term}
              lifetimeCost={lifetimeCost}
              monthlyPayment={payment}
              msrp={msrp}
              isShop4931ZeroDownRoute={isShop4931ZeroDownRoute}
            />
          </div>
          <Row className="align-items-stretch">
            <Col xs={12} md={6}>
              <AboutLease
                term={term}
                dueAtSigning={displayedDueAtSigning}
                mileageYearlyExcess={mileageYearlyExcess}
                trim={trim}
                endDate={endDate}
                lifetimeCost={lifetimeCost}
              />
            </Col>
            <Col xs={12} md={6}>
              <AboutVehicle
                msrp={msrp}
                engineType={engineType}
                driveType={driveType}
                electricityRange={electricityRange}
                combinedMpg={combinedMpg}
                seatingCapacity={seatingCapacity}
                edmundsRating={edmundsRating}
                makeNiceName={makeNiceName}
                modelNiceName={modelNiceName}
                year={year}
                styleId={styleId}
              />
            </Col>
          </Row>
          {disclaimer && (
            <>
              <h3 className="size-20 fw-bold text-cool-gray-10 mt-1_5 mb-0_5">Disclaimer</h3>
              {disclaimer.length < DISCLAIMER_COLLAPSE_THRESHOLD ? (
                <p className="mt-0_5 mt-md-0">{disclaimer}</p>
              ) : (
                <ContentCollapse
                  textClosed="Read more"
                  textOpen="Read less"
                  classes="mt-0_5 mt-md-0"
                  justify="justify-content-start"
                  btnContainerClasses="p-0 m-0"
                  btnClasses="p-0 m-0 size-16 more-info-btn text-shadow"
                  btnTextClasses="fw-normal"
                  showBtnBelow
                  trackingId="lease_deals_details"
                  collapseTrackingId="lease_deals_details"
                  isEclipseFade
                  minHeight="132px"
                  resetParams={{
                    offset: SCROLL_OFFSET,
                    duration: SCROLL_DURATION,
                    to: 'scrollTop-more-about',
                  }}
                >
                  {disclaimer}
                </ContentCollapse>
              )}
            </>
          )}
          {webUrls?.split(',').map(url => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="d-block mb-1 mt-1 text-cool-gray-30 size-16 text-decoration-underline"
            >
              See {make} Offer <i className="icon-open_in_new medium ms-0_25" aria-hidden />
            </a>
          ))}
        </div>
        <StickyLeaseDealsCta
          buttonToObserveRef={ctaRef}
          containerRef={scrollContainerRef}
          styleId={styleId}
          make={make}
          model={model}
          year={year}
          trim={trim}
          payment={payment}
          term={term}
          dueAtSigning={dueAtSigning}
          endDate={endDate}
          disclaimer={disclaimer}
          webUrls={webUrls}
          photo={photo}
          errorImage={errorImage}
          showCta={showCta}
          offerExpiryText={offerExpiryText}
        />
      </ModalBody>
    </Modal>
  );
}

LeaseDealsDetailsModal.propTypes = {
  id: PropTypes.string.isRequired,
  dueAtSigning: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  make: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
  msrp: PropTypes.number.isRequired,
  term: PropTypes.number.isRequired,
  trim: PropTypes.string,
  model: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  payment: PropTypes.number.isRequired,
  endDate: PropTypes.string.isRequired,
  webUrls: PropTypes.string,
  bodyType: PropTypes.string,
  photo: PropTypes.shape({}),
  driveType: PropTypes.string,
  engineType: PropTypes.string,
  disclaimer: PropTypes.string,
  makeNiceName: PropTypes.string.isRequired,
  lifetimeCost: PropTypes.number,
  modelNiceName: PropTypes.string.isRequired,
  seatingCapacity: PropTypes.number,
  mileageYearlyExcess: PropTypes.number,
  styleId: PropTypes.number.isRequired,
  effectivePaymentRatio: PropTypes.number.isRequired,
  effectivePayment: PropTypes.number.isRequired,
  effectivePaymentMedian: PropTypes.number,
  isShop4931ZeroDownRoute: PropTypes.bool,
  electricityRange: PropTypes.number,
  combinedMpg: PropTypes.number,
  edmundsRating: PropTypes.number,
  daysRemaining: PropTypes.number,
  showCta: PropTypes.bool,
};
